import { useState, useEffect } from 'react';
import { Users, Mail, Shield, Eye, MoreVertical, X, AlertCircle, UserPlus, Check, Trash2 } from 'lucide-react';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  profile_picture_url?: string;
  role: 'admin' | 'agent' | 'viewer';
  status: 'online' | 'away' | 'offline';
  email_verified: boolean;
  created_at: string;
}

interface TeamInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
  invited_by_name: string;
  invited_by_email: string;
}

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

export default function TeamManagement({ currentUser }: { currentUser: any }) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('agent');
  const [inviting, setInviting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    loadTeamData();
  }, []);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const loadTeamData = async () => {
    try {
      const tokens = localStorage.getItem('auth_tokens');
      if (!tokens) return;

      const { accessToken } = JSON.parse(tokens);

      const [membersRes, invitationsRes] = await Promise.all([
        fetch(`${API_URL}/api/team/members`, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/team/invitations`, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        }),
      ]);

      const membersData = await membersRes.json();
      const invitationsData = await invitationsRes.json();

      if (membersData.success) {
        setMembers(membersData.members);
      }
      if (invitationsData.success) {
        setInvitations(invitationsData.invitations);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      setErrorMessage('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail || !isAdmin) return;

    setInviting(true);
    try {
      const tokens = localStorage.getItem('auth_tokens');
      if (!tokens) return;

      const { accessToken } = JSON.parse(tokens);

      const res = await fetch(`${API_URL}/api/team/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });

      const data = await res.json();

      if (data.success) {
        setSuccessMessage(`Invitation sent to ${inviteEmail}`);
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('agent');
        await loadTeamData();
      } else {
        setErrorMessage(data.error || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      setErrorMessage('Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    if (!isAdmin) return;

    try {
      const tokens = localStorage.getItem('auth_tokens');
      if (!tokens) return;

      const { accessToken } = JSON.parse(tokens);

      const res = await fetch(`${API_URL}/api/team/members/${memberId}/role`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      const data = await res.json();

      if (data.success) {
        setSuccessMessage('Role updated successfully');
        await loadTeamData();
      } else {
        setErrorMessage(data.error || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      setErrorMessage('Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!isAdmin || !confirm(`Remove ${memberName} from the team?`)) return;

    try {
      const tokens = localStorage.getItem('auth_tokens');
      if (!tokens) return;

      const { accessToken } = JSON.parse(tokens);

      const res = await fetch(`${API_URL}/api/team/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      const data = await res.json();

      if (data.success) {
        setSuccessMessage('Member removed successfully');
        await loadTeamData();
      } else {
        setErrorMessage(data.error || 'Failed to remove member');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      setErrorMessage('Failed to remove member');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!isAdmin) return;

    try {
      const tokens = localStorage.getItem('auth_tokens');
      if (!tokens) return;

      const { accessToken } = JSON.parse(tokens);

      const res = await fetch(`${API_URL}/api/team/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      const data = await res.json();

      if (data.success) {
        setSuccessMessage('Invitation cancelled');
        await loadTeamData();
      } else {
        setErrorMessage(data.error || 'Failed to cancel invitation');
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setErrorMessage('Failed to cancel invitation');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'agent': return <Users className="w-4 h-4" />;
      case 'viewer': return <Eye className="w-4 h-4" />;
      default: return null;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-gray-100 text-gray-700';
      case 'agent': return 'bg-blue-100 text-blue-700';
      case 'viewer': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'away': return 'bg-yellow-500';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Team Members */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
            <p className="text-sm text-gray-500 mt-1">{members.length} member{members.length !== 1 ? 's' : ''}</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Invite Member
            </button>
          )}
        </div>

        <div className="divide-y divide-gray-200">
          {members.map((member) => {
            const isCurrentUser = member.id === currentUser?.id;
            return (
              <div key={member.id} className="px-6 py-4 flex items-center gap-4">
                {/* Profile Picture */}
                {member.profile_picture_url ? (
                  <img
                    src={member.profile_picture_url}
                    alt={member.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center text-white font-semibold text-lg">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                )}

                {/* Member Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {member.name}
                      {isCurrentUser && <span className="text-gray-500 ml-1">(You)</span>}
                    </p>
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(member.status)}`} title={member.status} />
                  </div>
                  <p className="text-sm text-gray-500 truncate">{member.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                      {getRoleIcon(member.role)}
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </span>
                    {!member.email_verified && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">Email not verified</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {isAdmin && !isCurrentUser && (
                  <div className="relative group">
                    <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <MoreVertical className="w-5 h-5 text-gray-500" />
                    </button>
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      <div className="py-1">
                        <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Change Role</div>
                        {['admin', 'agent', 'viewer'].map((role) => (
                          <button
                            key={role}
                            onClick={() => handleUpdateRole(member.id, role)}
                            disabled={member.role === role}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {getRoleIcon(role)}
                            {role.charAt(0).toUpperCase() + role.slice(1)}
                          </button>
                        ))}
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => handleRemoveMember(member.id, member.name)}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove Member
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Pending Invitations</h3>
            <p className="text-sm text-gray-500 mt-1">{invitations.length} pending</p>
          </div>

          <div className="divide-y divide-gray-200">
            {invitations.map((invitation) => (
              <div key={invitation.id} className="px-6 py-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-gray-400" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{invitation.email}</p>
                  <p className="text-sm text-gray-500">
                    Invited by {invitation.invited_by_name} • Expires{' '}
                    {new Date(invitation.expires_at).toLocaleDateString()}
                  </p>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium mt-1 ${getRoleBadgeColor(invitation.role)}`}>
                    {getRoleIcon(invitation.role)}
                    {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                  </span>
                </div>

                {isAdmin && (
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600"
                    title="Cancel invitation"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Invite Team Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="agent@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                >
                  <option value="agent">Agent - Can handle conversations</option>
                  <option value="admin">Admin - Full access</option>
                  <option value="viewer">Viewer - Read-only access</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
